import type { ColumnFiltersState, SortingState } from '@tanstack/react-table'
import {
  mapColumnFiltersToRolodexFilters,
  scopedTagsIdentityKey,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFilters'
import {
  mapSortingToOrderBy,
  withIdTiebreaker,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllOrderBy'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { parseSystemDocumentEntries } from 'features/knowledgeDocumentViewer/types/systemDocumentEntrySchemas'
import {
  useGetRolodexEntriesByLoadOptions,
  useRolodexLoadCount,
} from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import { RolodexEntryType, type RolodexFilterType } from 'types'

import {
  browsePageRequestKey,
  EMPTY_BROWSE_SORTING,
  useHeldBrowseDisplay,
} from './useHeldBrowseDisplay'

export const BROWSE_FILTER_FULL_LOAD_MAX = 1000

export type OffsetPagedLoadOptions = {
  type: RolodexEntryType[]
  tags: Record<string, string | string[]>
  offset: number
  limit: number
  order_by: ReturnType<typeof withIdTiebreaker>
  filters?: RolodexFilterType[]
}

export function resolveBrowsePageWindow(args: {
  hasFilters: boolean
  pageIndex: number
  pageSize: number
  totalCount: number | undefined
}): {
  limit: number
  loadFullFilteredSet: boolean
  offset: number
} {
  const pageIndex = Math.max(0, args.pageIndex)
  if (args.pageSize <= 0) {
    return { limit: 0, loadFullFilteredSet: false, offset: 0 }
  }
  if (
    args.hasFilters &&
    (args.totalCount === undefined ||
      args.totalCount < BROWSE_FILTER_FULL_LOAD_MAX)
  ) {
    return {
      limit: BROWSE_FILTER_FULL_LOAD_MAX,
      loadFullFilteredSet:
        args.totalCount !== undefined &&
        args.totalCount < BROWSE_FILTER_FULL_LOAD_MAX,
      offset: 0,
    }
  }
  return {
    limit: args.pageSize,
    loadFullFilteredSet: false,
    offset: pageIndex * args.pageSize,
  }
}

export function buildOffsetPagedSystemDocumentLoadOptions(args: {
  columnFilters: ColumnFiltersState | undefined
  limit: number
  offset: number
  scopedTags: Record<string, string | string[]> | undefined
  sorting: SortingState | undefined
}): OffsetPagedLoadOptions | undefined {
  if (!args.scopedTags || args.limit <= 0) return undefined
  const filters = mapColumnFiltersToRolodexFilters(args.columnFilters)
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    tags: args.scopedTags,
    offset: Math.max(0, args.offset),
    limit: args.limit,
    order_by: withIdTiebreaker(mapSortingToOrderBy(args.sorting)),
    ...(filters.length > 0 ? { filters } : {}),
  }
}

export function buildOffsetPagedSystemDocumentCountOptions(args: {
  columnFilters: ColumnFiltersState | undefined
  scopedTags: Record<string, string | string[]> | undefined
}): Pick<OffsetPagedLoadOptions, 'filters' | 'tags' | 'type'> | undefined {
  if (!args.scopedTags) return undefined
  const filters = mapColumnFiltersToRolodexFilters(args.columnFilters)
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    tags: args.scopedTags,
    ...(filters.length > 0 ? { filters } : {}),
  }
}

export function useOffsetPagedSystemDocuments(args: {
  columnFilters: ColumnFiltersState | undefined
  enabled: boolean
  pageIndex: number
  pageSize: number
  scopedTags: Record<string, string | string[]> | undefined
  sorting: SortingState | undefined
}): {
  displayedPageIndex: number
  displayedSorting: SortingState
  error: Error | undefined
  isFetching: boolean
  isLoading: boolean
  loadFullFilteredSet: boolean
  pageEntries: SystemDocumentEntry[] | undefined
  refresh: () => Promise<void>
  totalCount: number | undefined
} {
  const countOptions = useMemo(
    () =>
      args.enabled
        ? buildOffsetPagedSystemDocumentCountOptions({
            columnFilters: args.columnFilters,
            scopedTags: args.scopedTags,
          })
        : undefined,
    [args.columnFilters, args.enabled, args.scopedTags]
  )
  const {
    data: totalCount,
    error: countError,
    isLoading: isLoadingCount,
    isValidating: isValidatingCount,
    mutate: refreshCount,
  } = useRolodexLoadCount(countOptions)

  const hasFilters =
    mapColumnFiltersToRolodexFilters(args.columnFilters).length > 0
  const pageWindow = useMemo(
    () =>
      resolveBrowsePageWindow({
        hasFilters,
        pageIndex: args.pageIndex,
        pageSize: args.pageSize,
        totalCount,
      }),
    [args.pageIndex, args.pageSize, hasFilters, totalCount]
  )
  const loadOptions = useMemo(
    () =>
      args.enabled
        ? buildOffsetPagedSystemDocumentLoadOptions({
            columnFilters: args.columnFilters,
            limit: pageWindow.limit,
            offset: pageWindow.offset,
            scopedTags: args.scopedTags,
            sorting: args.sorting,
          })
        : undefined,
    [
      args.columnFilters,
      args.enabled,
      args.scopedTags,
      args.sorting,
      pageWindow.limit,
      pageWindow.offset,
    ]
  )

  const {
    data: rawPageEntries,
    error: pageError,
    isLoading: isLoadingPage,
    isValidating: isValidatingPage,
    mutate: refreshPage,
  } = useGetRolodexEntriesByLoadOptions<unknown>(loadOptions)

  const pageEntries = useMemo(
    () =>
      rawPageEntries === undefined
        ? undefined
        : parseSystemDocumentEntries(rawPageEntries),
    [rawPageEntries]
  )
  const resolvedTotalCount =
    totalCount ??
    (countError !== undefined && pageEntries !== undefined
      ? pageEntries.length
      : undefined)

  const refresh = useCallback(async () => {
    await Promise.all([refreshPage(), refreshCount()])
  }, [refreshCount, refreshPage])

  const pageFresh =
    pageError === undefined && pageEntries !== undefined && !isLoadingPage
  const countReady =
    countError !== undefined ||
    (resolvedTotalCount !== undefined && !isLoadingCount)

  const browseIdentity = `${args.enabled}:${scopedTagsIdentityKey(args.scopedTags)}`
  const displayed = useHeldBrowseDisplay({
    browseIdentity,
    countReady,
    hasPageError: pageError !== undefined,
    pageEntries,
    pageFresh,
    pageIndex: args.pageIndex,
    requestKey: browsePageRequestKey(loadOptions),
    sorting: args.sorting,
    totalCount: resolvedTotalCount ?? pageEntries?.length ?? 0,
  })

  return {
    displayedPageIndex: displayed?.pageIndex ?? 0,
    displayedSorting: displayed?.sorting ?? EMPTY_BROWSE_SORTING,
    error: pageError,
    isFetching: isValidatingPage || isValidatingCount,
    isLoading:
      loadOptions !== undefined &&
      displayed === undefined &&
      pageError === undefined,
    loadFullFilteredSet: pageWindow.loadFullFilteredSet,
    pageEntries: displayed?.entries,
    refresh,
    totalCount: displayed?.totalCount,
  }
}
